/**
 * AuditSinkForm — inline animated form for adding a new audit sink.
 *
 * Handles sink type selection, dynamic config fields, test-connection,
 * and save. Animates open/closed — wrap in <AnimatePresence> at the call site.
 *
 * Props:
 *   onCancel  – called when user cancels or save completes
 *   onCreated – called with sinkId after a successful save
 */

import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Database, FlaskConical, CheckCircle2, XCircle } from "lucide-react";
import useFeedback from "../../hooks/useFeedback";
import Input from "../Inputs/Input";
import { PrimaryButton, SecondaryButton } from "../Button/Buttons";
import { FormLabel } from "../Labels/labels";
import {
  createAuditSink,
  testAuditSink,
  deleteAuditSink,
} from "../../api/adminAuditSinks";

// ─── Constants ────────────────────────────────────────────────────────────────

export const SINK_LABELS = {
  pubsub: "Pub/Sub",
  bigquery: "BigQuery",
  gcs: "Cloud Storage (GCS)",
};

export const SINK_DESCRIPTIONS = {
  pubsub: "Stream events in real time to a Pub/Sub topic.",
  bigquery: "Insert events via streaming API into a BigQuery table.",
  gcs: "Buffer events and flush to a GCS bucket as NDJSON files.",
};

// ─── Config field components per sink type ────────────────────────────────────

const PubSubConfigFields = ({ config, onChange }) => (
  <div className="flex flex-col gap-1">
    <FormLabel>Topic name</FormLabel>
    <Input
      placeholder="projects/my-project/topics/my-audit-topic"
      value={config.topic_name ?? ""}
      onChange={(e) => onChange({ ...config, topic_name: e.target.value })}
    />
  </div>
);

const BigQueryConfigFields = ({ config, onChange }) => (
  <>
    <div className="flex flex-col gap-1">
      <FormLabel>Project ID</FormLabel>
      <Input
        placeholder="my-gcp-project"
        value={config.project_id ?? ""}
        onChange={(e) => onChange({ ...config, project_id: e.target.value })}
      />
    </div>
    <div className="flex flex-col gap-1">
      <FormLabel>Dataset ID</FormLabel>
      <Input
        placeholder="audit_logs"
        value={config.dataset_id ?? ""}
        onChange={(e) => onChange({ ...config, dataset_id: e.target.value })}
      />
      {config.dataset_id && !/^[a-zA-Z0-9_]+$/.test(config.dataset_id) && (
        <p className="text-xs text-ink-red-600 dark:text-ink-red-300">
          Dataset IDs must be alphanumeric and underscores only — no hyphens or spaces.
        </p>
      )}
    </div>
    <p className="text-xs text-ink-gray-400 mt-1">
      Photon will automatically create a <code className="font-mono">photon_audit_events</code> table
      inside this dataset if it does not already exist.
    </p>
  </>
);

const GCSConfigFields = ({ config, onChange }) => (
  <>
    <div className="flex flex-col gap-1">
      <FormLabel>Bucket name</FormLabel>
      <Input
        placeholder="my-audit-bucket"
        value={config.bucket_name ?? ""}
        onChange={(e) => onChange({ ...config, bucket_name: e.target.value })}
      />
    </div>
    <div className="flex flex-col gap-1">
      <FormLabel>Prefix (optional)</FormLabel>
      <Input
        placeholder="photon-audit/"
        value={config.prefix ?? ""}
        onChange={(e) => onChange({ ...config, prefix: e.target.value })}
      />
    </div>
    <div className="flex flex-col gap-1">
      <FormLabel>Batch window</FormLabel>
      <select
        value={config.batch_window ?? "hourly"}
        onChange={(e) => onChange({ ...config, batch_window: e.target.value })}
        className="photon-field w-full rounded-xl border border-edge-gray-200 dark:border-edge-gray-700 bg-fill-white dark:bg-fill-gray-800 text-sm px-3 py-2 text-ink-gray-900 dark:text-ink-gray-50 focus:outline-none focus:ring-2 focus:ring-focus-indigo-600"
      >
        <option value="hourly">Hourly</option>
        <option value="daily">Daily</option>
      </select>
    </div>
  </>
);

// ─── AuditSinkForm ────────────────────────────────────────────────────────────

const AuditSinkForm = ({ onCancel, onCreated }) => {
  const { showFeedback } = useFeedback();
  const [sinkType, setSinkType] = useState("pubsub");
  const [config, setConfig] = useState({});
  const [testing, setTesting] = useState(false);
  const [testStatus, setTestStatus] = useState(null); // null | "pass" | "fail"
  const [testError, setTestError] = useState(null);
  const [saving, setSaving] = useState(false);

  const handleTest = async () => {
    setTesting(true);
    setTestStatus(null);
    setTestError(null);
    try {
      const sinkId = await createAuditSink(sinkType, config, false);
      try {
        await testAuditSink(sinkId);
        setTestStatus("pass");
      } catch (err) {
        setTestStatus("fail");
        setTestError(err?.response?.data?.error ?? "Connection test failed — check your config and IAM permissions.");
      } finally {
        await deleteAuditSink(sinkId).catch(() => null);
      }
    } catch (err) {
      setTestStatus("fail");
      setTestError(err?.response?.data?.error ?? "Could not test connection.");
    } finally {
      setTesting(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const sinkId = await createAuditSink(sinkType, config, true);
      showFeedback("Audit sink added.", "success");
      onCreated(sinkId);
      onCancel();
    } catch (err) {
      showFeedback(err?.response?.data?.error ?? "Failed to add sink — check IAM permissions.", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <motion.div
      key="inline-add-sink-form"
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: "auto" }}
      exit={{ opacity: 0, height: 0 }}
      transition={{ duration: 0.2 }}
      className="overflow-hidden"
    >
      <div className="bg-fill-white dark:bg-fill-dialog-surface border border-edge-gray-200 dark:border-edge-dialog-border rounded-xl mb-4 p-5 flex flex-col gap-5">
        <p className="text-xs font-semibold text-ink-gray-500 dark:text-ink-gray-400 uppercase tracking-wide">
          New Audit Sink
        </p>

        {/* Sink type selector */}
        <div className="flex flex-col gap-1.5">
          <FormLabel>Sink type</FormLabel>
          <div className="grid grid-cols-3 gap-2">
            {Object.entries(SINK_LABELS).map(([key, label]) => (
              <button
                key={key}
                onClick={() => { setSinkType(key); setConfig({}); }}
                className={`flex flex-col items-center gap-1 rounded-xl border p-3 text-sm transition-colors ${
                  sinkType === key
                    ? "border-edge-indigo-600 bg-fill-indigo-50 dark:bg-fill-indigo-800/20 text-ink-indigo-600"
                    : "border-edge-gray-200 dark:border-edge-gray-700 text-ink-gray-500 hover:border-edge-indigo-600"
                }`}
              >
                <Database size={18} />
                {label}
              </button>
            ))}
          </div>
          <p className="text-xs text-ink-gray-500 dark:text-ink-gray-400 mt-0.5">
            {SINK_DESCRIPTIONS[sinkType]}
          </p>
        </div>

        {/* Dynamic config fields */}
        {sinkType === "pubsub" && <PubSubConfigFields config={config} onChange={setConfig} />}
        {sinkType === "bigquery" && <BigQueryConfigFields config={config} onChange={setConfig} />}
        {sinkType === "gcs" && <GCSConfigFields config={config} onChange={setConfig} />}

        {/* Actions */}
        <div className="flex flex-col gap-2">
          <div className="flex gap-3 justify-between">
            <SecondaryButton
              onClick={handleTest}
              disabled={testing || saving}
              className="flex items-center gap-2"
            >
              <FlaskConical size={14} />
              {testing ? "Testing…" : "Test Connection"}
            </SecondaryButton>
            <div className="flex gap-3">
              <SecondaryButton onClick={onCancel} disabled={saving || testing}>
                Cancel
              </SecondaryButton>
              <PrimaryButton onClick={handleSave} disabled={saving || testing}>
                {saving ? "Saving…" : "Add Sink"}
              </PrimaryButton>
            </div>
          </div>

          {/* Runway lights while testing */}
          <AnimatePresence>
            {testing && (
              <motion.div
                key="runway"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="flex items-center gap-1.5 px-1 pt-1"
              >
                {[0, 1, 2, 3, 4, 5].map((i) => (
                  <motion.span
                    key={i}
                    className="w-2 h-2 rounded-full bg-fill-primary"
                    animate={{ opacity: [0.2, 1, 0.2] }}
                    transition={{
                      duration: 1.2,
                      repeat: Infinity,
                      delay: i * 0.15,
                      ease: "easeInOut",
                    }}
                  />
                ))}
                <span className="text-xs text-ink-gray-500 dark:text-ink-gray-400 ml-1">Verifying connection…</span>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Result */}
          <AnimatePresence>
            {!testing && testStatus === "pass" && (
              <motion.div
                key="pass"
                initial={{ opacity: 0, y: -4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className="flex items-center gap-2 text-sm text-ink-green-600 dark:text-ink-green-300 px-1"
              >
                <CheckCircle2 size={16} />
                Connection verified successfully.
              </motion.div>
            )}
            {!testing && testStatus === "fail" && (
              <motion.div
                key="fail"
                initial={{ opacity: 0, y: -4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className="flex flex-col gap-1 px-1"
              >
                <div className="flex items-center gap-2 text-sm text-ink-red-600 dark:text-ink-red-300">
                  <XCircle size={16} />
                  Connection test failed.
                </div>
                {testError && (
                  <p className="text-xs text-ink-red-600/80 dark:text-ink-red-300/80 ml-6 font-mono break-all">
                    {testError}
                  </p>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </motion.div>
  );
};

export default AuditSinkForm;
